/**
 * One warning letter, by reference number (owner, 2026-11-04). Reached from the timesheet remarks and
 * the warning history. The employee sees their own, the direct manager their direct reports', HR roles
 * all; the server refuses everyone else. Deduction amounts only for salary roles.
 * Backend: GET /api/warnings/ref/{reference_no}.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { FileWarning, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const STATUS = { active: ['Active', 'ساري'], cancelled: ['Cancelled', 'ملغى'], archived: ['Archived', 'مؤرشف'], deleted: ['Deleted', 'محذوف'] };
const DED_STATUS = { pending: ['Awaiting HR Manager approval', 'بانتظار اعتماد مدير الموارد البشرية'], approved: ['Approved', 'معتمد'], rejected: ['Rejected', 'مرفوض'] };

export default function WarningDetailsPage() {
  const { ref } = useParams();
  const { token } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = (en, ar) => (isRTL ? ar : en);
  const [w, setW] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/warnings/ref/${encodeURIComponent(ref)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setW(r.data))
      .catch((e) => setError(e?.response?.status === 403 ? t('You cannot view this warning.', 'لا يمكنك عرض هذا الإنذار.')
        : e?.response?.status === 404 ? t('No warning with this reference.', 'لا يوجد إنذار بهذا المرجع.') : e.message));
  }, [ref, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const row = (label, value) => (
    <div className="grid grid-cols-3 gap-2 border-b py-2 text-sm"><div className="text-slate-500">{label}</div><div className="col-span-2">{value ?? '—'}</div></div>
  );
  const d = w?.deduction;
  return (
    <div className="p-6 max-w-2xl space-y-4" dir={isRTL ? 'rtl' : 'ltr'} data-testid="warning-details-page">
      <div className="flex items-center gap-2">
        <FileWarning className="h-6 w-6 text-amber-600" />
        <h1 className="text-xl font-bold">{t('Warning letter', 'خطاب إنذار')} · <span className="font-mono">{ref}</span></h1>
      </div>
      {error && <p className="text-rose-700" role="alert">{error}</p>}
      {!w && !error && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
      {w && (
        <div className="rounded border bg-white p-4">
          {row(t('Employee', 'الموظف'), `${(isRTL ? w.employee?.name_ar : w.employee?.name) || w.employee?.name || '—'}${w.employee?.employee_number ? ` · ${w.employee.employee_number}` : ''}`)}
          {row(t('Level', 'المستوى'), t(`${w.level.en} warning`, `إنذار ${w.level.ar}`))}
          {row(t('Status', 'الحالة'), t(...(STATUS[w.status] || [w.status, w.status])))}
          {row(t('Issue date', 'تاريخ الإصدار'), w.issue_date)}
          {row(t('Violation date', 'تاريخ المخالفة'), w.violation_date)}
          {row(t('Reason', 'السبب'), w.reason)}
          {row(t('Violation reference', 'مرجع المخالفة'), w.violation_reference)}
          {w.cancellation_reason && w.status !== 'active' && row(t('Cancellation', 'الإلغاء'), w.cancellation_reason)}
          {row(t('Deduction', 'الخصم'), d ? (
            <span>
              {d.amount != null ? `${d.amount} SAR` : (d.has_amount ? t('amount shown to salary roles only', 'المبلغ يظهر لصلاحيات الرواتب فقط') : '')}
              {d.days ? ` ${d.has_amount ? '+ ' : ''}${d.days} ${t('day(s)', 'يوم')}` : ''}
              {' · '}{t(...(DED_STATUS[d.status] || [d.status, d.status]))} · {t('pay period', 'فترة الراتب')} {d.pay_period}
              <div className="text-xs text-slate-500">{t('One-time: the basic salary is not changed.', 'لمرة واحدة: لا يتغير الراتب الأساسي.')}</div>
            </span>
          ) : t('None', 'لا يوجد'))}
          {w.source === 'document' && w.id && (
            <div className="pt-3 text-sm"><Link className="text-blue-700 underline" to={`/documents/${w.id}`}>{t('Open the letter', 'فتح الخطاب')}</Link></div>
          )}
        </div>
      )}
    </div>
  );
}
